import * as Yup from "yup";

export interface BirthFormValues {
    day: string;
    month: string;
    year: string;
    date: string;
}

export const BIRTHDAY_ADULT_MESSAGE = 'You must be 18 or over.';
export const DEFAULT_BIRTHDAY = '2000-10-12';

const padDatePart = (value: string) => value.padStart(2, '0');

export const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = padDatePart(String(date.getMonth() + 1));
  const day = padDatePart(String(date.getDate()));
  return `${year}-${month}-${day}`;
};

export const latestAdultBirthday = (now = new Date()) =>
  new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());

export const earliestAllowedBirthday = (now = new Date()) =>
  new Date(now.getFullYear() - 100, now.getMonth(), now.getDate());

export const parseBirthDate = (value?: string | null) => {
  if (!value) return null;
  const cleanedValue = value.trim();
  const dateOnlyMatch = cleanedValue.match(/^\d{4}-\d{2}-\d{2}/);
  const normalizedValue = dateOnlyMatch ? dateOnlyMatch[0] : cleanedValue;
  const parts = normalizedValue.split(/[/-]/);
  if (parts.length !== 3) return null;

  const [first, second, third] = parts;
  const yearFirst = first.length === 4;
  const year = Number(yearFirst ? first : third);
  const month = Number(yearFirst ? second : second);
  const day = Number(yearFirst ? third : first);

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
};

export const normalizeBirthDateValue = (value?: string | null) => {
  const parsed = parseBirthDate(value);
  return parsed ? formatDateInputValue(parsed) : DEFAULT_BIRTHDAY;
};

export const isAdultBirthday = (value?: string | null, now = new Date()) => {
  const parsed = parseBirthDate(value);
  if (!parsed) return false;
  return parsed.getTime() <= latestAdultBirthday(now).getTime();
};

export const isNotOlderThan100 = (value?: string | null, now = new Date()) => {
  const parsed = parseBirthDate(value);
  if (!parsed) return false;
  return parsed.getTime() >= earliestAllowedBirthday(now).getTime();
};

export const birthValidationSchema = Yup.object({
  day: Yup
    .string()
    .required('Required')
    .matches(/^(0[1-9]|[12][0-9]|3[01])$/, 'Invalid day')
    .max(2, 'Day must have a maximum of 2 characters'),
  month: Yup
    .string()
    .required('Required')
    .matches(/^(0[1-9]|1[0-2])$/, 'Invalid month')
    .max(2, 'Month must have a maximum of 2 characters'),
  year: Yup
    .string()
    .required('Required')
    .matches(/^(19|20)\d{2}$/, 'Invalid year')
    .max(4, 'Year must have a maximum of 4 characters'),
  date: Yup.string()
    .required('Required')
    .test('is-valid-date', 'Invalid date', (value) => Boolean(parseBirthDate(value)))
    .test('is-adult', BIRTHDAY_ADULT_MESSAGE, (value) => isAdultBirthday(value))
    .test('is-not-too-old', 'Please enter a valid birthday.', (value) =>
      isNotOlderThan100(value),
    ),
});
